import { forwardRef } from '@chakra-ui/react';
import { type MouseEvent, useState } from 'react';

import {
  Box,
  type BoxProps,
  IconButton,
  type IconButtonProps,
} from '@frontend/components';
import {
  CaretDownIcon,
  ChevronDownIcon,
} from '@frontend/components/src/components/mediaAndIcons/icons/iconSet';

import type { TableData, TableRecord, TableRecordKey } from '../types';

type ExpandIconProps = IconButtonProps &
  BoxProps & {
    record: TableRecord;
    expanded?: boolean;
    expandRowByClick?: boolean;
    expandable?: boolean;
    prefixCls?: string;
    onExpand: (record: TableRecord, e: MouseEvent) => void;
  };

/**
 * A component to be used as a timesaver for child expansion
 */
const ChildExpandIcon = forwardRef<ExpandIconProps, 'button'>(
  ({ expandRowByClick, expanded, onExpand, record, ...restProps }, ref) => {
    // Exempt invalid DOM props from hitting the chakra components.
    // eslint-disable-next-line no-unused-vars
    const { prefixCls, expandable, ...domValidProps } = restProps;

    return (
      <>
        {record.children != null ? (
          <IconButton
            ref={ref}
            onClick={!expandRowByClick ? (e) => onExpand(record, e) : undefined}
            marginRight={0.5}
            {...domValidProps}
          >
            <CaretDownIcon
              transition="transform 0.25s"
              transform={expanded ? 'unset' : 'rotate(-90deg)'}
            />
          </IconButton>
        ) : (
          <Box
            ref={ref}
            display="inline-flex"
            w="20px"
            marginRight={1}
            {...domValidProps}
          />
        )}
      </>
    );
  },
);
ChildExpandIcon.displayName = 'ChildExpandIcon';

const ExpandedRowExpandIcon = forwardRef<ExpandIconProps, 'button'>(
  ({ expandRowByClick, expanded, onExpand, record, ...restProps }, ref) => {
    // Exempt invalid DOM props from hitting IconButton.
    // eslint-disable-next-line no-unused-vars
    const { prefixCls, expandable, ...domValidProps } = restProps;

    return (
      <IconButton
        ref={ref}
        size="md"
        onClick={!expandRowByClick ? (e) => onExpand(record, e) : undefined}
        {...domValidProps}
      >
        <ChevronDownIcon
          transition="transform 0.25s"
          transform={expanded ? 'unset' : 'rotate(-90deg)'}
        />
      </IconButton>
    );
  },
);
ExpandedRowExpandIcon.displayName = 'ExpandedRowExpandIcon';

/**
 * A function that maintains a single expanded row (exclusivity) among highest-level rows of the table.
 */
const collapseOthersExpand = (
  isExpanded: boolean,
  record: TableRecord,
  data: TableData,
  expandedRowKeys: TableRecordKey[],
) => {
  // Nullish coalescing operator (??) ensures all children are expandable (and no console errors) if data is not passed in.
  const hasChildren = data?.some((record) => record.children != null) ?? true;

  // If we have children, we maintain exclusivity only among parents.
  // Using the default method below this causes an issue where opening a child closes its parent.
  if (hasChildren) {
    const allParents = data?.map((indexedRecord) => indexedRecord.key);

    // See above comment about Nullish coalescing operator (??).
    const isParent = data?.some(
      (indexedRecord) => indexedRecord.key === record.key ?? true,
    );
    if (isExpanded) {
      if (isParent) {
        // If we're a parent and we're expanding --> collapse all other parents and add us.
        return [
          ...expandedRowKeys.filter((el) => !allParents.includes(el)),
          record.key,
        ];
      }
      // If we're not a parent and we are expanding --> add us to the list.
      return [...expandedRowKeys, record.key];
    }
    // Regardless of parental status --> remove us from the list.
    return expandedRowKeys.filter((el) => el != record.key);
  }
  // If we don't have children, we can just maintain exclusivity by keeping a single record in the store and skip all the array indexing logic.

  return isExpanded ? [record.key] : [];
};

/**
 * A hook that drives standard table expansion behavior.
 * @param {[string]} data the data you would use to populate the table.
 * @param {boolean} isSingleExpansion determines if only a single high-level row should be expanded at a time.
 * @returns {Object} `{ expanded, onExpand, ChildExpandIcon, childIndentSize }` an object containing an array of expanded string keys, a function to call on expanding a single row, an IconButton for child expansion, and a recommended child indent size.
 */
export const useRCTableExpansion = (
  data: TableData,
  isSingleExpansion: boolean,
) => {
  const [expanded, setExpanded] = useState<TableRecordKey[]>([]);
  /**
   * a function that translates the onExpand output of the table into the correct expanded keys.
   */
  function onExpand(isExpanded: boolean, record: TableRecord) {
    // If single expansion (exclusivity among highest-level expansions) is turned on:
    if (isSingleExpansion) {
      // Run our custom logic to set the expanded keys to ensure this.
      setExpanded(collapseOthersExpand(isExpanded, record, data, expanded));
    }
    // If single expansion is turned off:
    else {
      // If this attempt is expanding,
      if (isExpanded) {
        // add the key of the record to the expansion.
        setExpanded([...expanded, record.key]);
      } else {
        // remove the key of the record from the expanded set.
        setExpanded(expanded.filter((el) => el != record.key));
      }
    }
  }

  return {
    expanded,
    onExpand,
    ChildExpandIcon,
    ExpandedRowExpandIcon,
    childIndentSize: 24,
  };
};
